import React, { useMemo } from 'react';

import {
  IonBadge,
  IonButtons,
  IonIcon,
  IonItem,
  IonLabel,
  IonSpinner,
} from '@ionic/react';
import { caretDownOutline, closeCircleOutline } from 'ionicons/icons';

import clsx from 'clsx';

import Button from 'components/Button';
import Typography from 'components/Typography';

import { useModal } from '../Modal';
import { SelectPicker } from './SelectPicker';
import './index.css';
import { ISelect } from './interfaces';

const Select = ({
  isLoading = false,
  onChange,
  data,
  multiple = false,
  value = multiple ? [] : undefined,
  clearable = false,
  clearableBlock = clearable,
  displayMultipleLimit = 2,
  placeholder: placeholderProp,
  getLabel = ({ name }) => name,
  getBadge,
  label,
  pickerTitle = label,
  error,
  disabled,
  helper,
  className = '',
  variant = 'stacked',
  dense,
  ModalComponent = SelectPicker,
  modalParams = {},
}: ISelect) => {
  const displaySingle: string = useMemo(() => {
    if (multiple) {
      return '';
    }
    const option = data?.find((opt: any) => opt.id === value);
    if (option) {
      return getLabel(option) || '';
    }
    return '';
  }, [value, data]);

  const displayMultiple: string[] = useMemo(() => {
    if (!multiple) {
      return [];
    }
    const options: any[] =
      data?.filter((opt: any) => value.includes(opt.id)) || [];
    return options.map(getLabel);
  }, [value, data]);

  const allSelected = multiple
    ? data?.length === displayMultiple.length
    : false;

  const display: string = useMemo(() => {
    if (multiple) {
      if (displayMultiple.length)
        return allSelected
          ? '' // if all selected show placeholder
          : displayMultiple.length < displayMultipleLimit
          ? displayMultiple.join(', ')
          : `${displayMultiple.length} selected`;
    }
    return displaySingle;
  }, [
    multiple,
    displaySingle,
    displayMultiple,
    displayMultipleLimit,
    allSelected,
  ]);

  const [openSelectPicker] = useModal(ModalComponent, {
    data: data,
    value: value,
    error: error,
    isLoading: isLoading,
    getBadge: getBadge,
  });

  const handleOpen = () => {
    openSelectPicker({
      title: pickerTitle,
      multiple: multiple,
      getLabel: getLabel,
      onSubmit: (v: any) => {
        onChange(
          {
            target: {
              value: v,
            },
          },
          v
        );
      },
      ...modalParams,
    });
  };

  const handleClear = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const clearedValue = multiple ? [] : undefined;
    onChange(
      {
        target: {
          value: clearedValue,
        },
      },
      clearedValue
    );
  };
  const rootBadge = useMemo(() => {
    if (getBadge) {
      return getBadge({ id: value });
    }
  }, [getBadge, value]);

  const isShowClearButton = useMemo(() => {
    if (multiple) {
      return clearable && value.length > 0;
    }
    return clearable && value !== undefined;
  }, [clearable, value]);

  const isStacked = variant === 'stacked';
  return (
    <IonItem
      lines={'none'}
      disabled={disabled}
      className={clsx('ion-no-padding select-container', {
        dense: dense,
      })}
    >
      <IonItem
        onClick={handleOpen}
        lines={dense ? 'none' : 'inset'}
        className={clsx('select-item', className, {
          'ion-invalid': !!error,
          dense: dense,
        })}
      >
        {label ? (
          <IonLabel position={isStacked ? 'stacked' : 'fixed'}>
            <Typography
              variant={isStacked ? 'overline' : 'subtitle1'}
              color={disabled ? 'dark' : 'medium-shade'}
            >
              {label}
            </Typography>
          </IonLabel>
        ) : null}

        <div className={'display-wrapper'}>
          <div className={'display'}>
            <Typography
              variant={'subtitle1'}
              color={display ? 'dark' : 'medium-tint'}
              lineClamp={1}
            >
              {display || placeholderProp || (multiple ? 'All' : 'Select')}
            </Typography>
          </div>

          {rootBadge ? (
            <IonBadge color={rootBadge.color} slot={'end'} className={'badge'}>
              {rootBadge.label}
            </IonBadge>
          ) : null}

          {isLoading ? (
            <IonSpinner
              slot={'end'}
              name={'crescent'}
              color={disabled ? 'medium' : 'medium-shade'}
              className={'spinner'}
            />
          ) : (
            <IonIcon
              slot={'end'}
              icon={caretDownOutline}
              color={disabled ? 'medium' : 'medium-shade'}
              className={'caret'}
            />
          )}
        </div>

        {helper && (
          <Typography
            slot={'helper'}
            variant={'caption'}
            color={disabled ? 'dark' : 'medium-tint'}
            className={'ion-padding-horizontal'}
          >
            {helper}
          </Typography>
        )}
        {error && label && (
          <Typography
            slot={'error'}
            variant={'caption'}
            color={'danger'}
            className={'ion-padding-horizontal'}
          >
            {error}
          </Typography>
        )}
      </IonItem>
      {clearableBlock && (
        <IonButtons
          slot={'end'}
          className={'ion-no-margin clear-icon-button-container'}
        >
          {isShowClearButton && (
            <Button
              slot={'icon-only'}
              fill={'clear'}
              onClick={handleClear}
              className={'clear-icon-button'}
            >
              <IonIcon icon={closeCircleOutline} color={'medium'} />
            </Button>
          )}
        </IonButtons>
      )}
    </IonItem>
  );
};
export default Select;
